import React from 'react';
import '../../../styles/PrintProgram.css';
import { formatCurrency } from '../ProgramForm/helpers/quotationCalculations';

const PrintProgram = ({
  program,
  lang = 'en',
  clientInfo = null,
  pdfSections = null,
  includePrices = true
}) => {
  console.log("🚀 ~ PrintProgram ~ clientInfo:", clientInfo)
  // ✅ تحويل الصورة إلى Base64
  const coverImageUrl = program?.cover_image?.url;

  // ==================== Helper Functions ====================

  const formatDate = (date) => {
    if (!date) return '';
    try {
      return new Date(date).toLocaleDateString(
        lang === 'ar' ? 'ar-EG' : 'en-US',
        { day: 'numeric', month: 'long', year: 'numeric' }
      );
    } catch (error) {
      console.error('Error formatting date:', error);
      return '';
    }
  };

  const formatTime = (timeString) => {
    if (!timeString) return '-';
    try {
      const timeStr = String(timeString).trim();

      if (/^\d{2}:\d{2}(:\d{2})?$/.test(timeStr)) {
        return timeStr.substring(0, 5);
      }

      if (/^\d{1,2}:\d{2}:\d{2}$/.test(timeStr)) {
        return timeStr.substring(0, 5);
      }

      let cleanTime = timeStr.toUpperCase();
      const ampm = cleanTime.match(/(AM|PM)$/) ? cleanTime.slice(-2) : '';

      if (ampm) {
        cleanTime = cleanTime.replace(/\s*(AM|PM)$/i, '').trim();
      }

      const timeParts = cleanTime.split(':');
      let hours = parseInt(timeParts[0]) || 0;
      const minutes = parseInt(timeParts[1]) || 0;

      if (ampm === 'PM' && hours !== 12) {
        hours += 12;
      } else if (ampm === 'AM' && hours === 12) {
        hours = 0;
      }

      const formattedHours = String(hours).padStart(2, '0');
      const formattedMinutes = String(minutes).padStart(2, '0');

      return `${formattedHours}:${formattedMinutes}`;
    } catch (error) {
      console.warn('Error formatting time:', timeString, error);
      return String(timeString).trim() || '-';
    }
  };

  const getProductTime = (product) => {
    if (!product) return '-';
    const timeValue = product.time || product.pickup_time;
    return formatTime(timeValue);
  };

  const t = (field) => {
    if (!field) return '';
    if (typeof field === 'string') return field;
    return field[lang] || field.en || field.ar || '';
  };

  const getPaxQty = (searchName) => {
    const paxTypes = program.pax_types || program.paxtypes || [];

    if (!paxTypes || paxTypes.length === 0) {
      return 0;
    }

    const paxType = paxTypes.find((p) => {
      const nameEn = p.name?.en || p.name_en || '';
      const nameAr = p.name?.ar || p.name_ar || '';
      const nameString = typeof p.name === 'string' ? p.name : '';

      const searchLower = searchName.toLowerCase();

      return (
        nameEn.toLowerCase() === searchLower ||
        nameAr === searchName ||
        nameString.toLowerCase() === searchLower ||
        nameEn.toLowerCase().includes(searchLower) ||
        nameString.toLowerCase().includes(searchLower)
      );
    });

    return paxType?.quantity ?? paxType?.count ?? 0;
  };

  const getProductSummary = (product) => {
    if (!product) return '';
    const field = product.summary || product.short_description;
    if (!field) return '';
    if (typeof field === 'string') return field;
    return field[lang] || field.en || field.ar || '';
  };

  const getPricingCode = (product) => {
    if (!product) return '';
    return product.pricing_code || product.pricingCode || '';
  };

  const getProductCity = (product) => {
    if (!product) return '';
    const cityObj = product.city;
    if (!cityObj) return '';
    if (typeof cityObj === 'string') return cityObj;
    return cityObj.name?.[lang] || cityObj.name?.en || cityObj.name?.ar || '';
  };

  const isDayLarge = (day) => {
    return day.products && day.products.length > 5;
  };

  const formatCities = (cities, locale) => {
    if (!cities || !Array.isArray(cities) || cities.length === 0) return '';
    const names = cities
      .sort((a, b) => a.order - b.order)
      .map(c => `\u2068${c.name?.[locale] || c.name?.en || c.name?.ar || ''}\u2069`);

    if (names.length === 0) return '';
    if (names.length === 1) return names[0];

    const separator = locale === 'ar' ? '، ' : ', ';
    const lastSeparator = locale === 'ar' ? ' و' : ' & ';

    return names.slice(0, -1).join(separator) + lastSeparator + names[names.length - 1];
  };

  // ==================== النصوص المترجمة ====================

  const translations = {
    en: {
      companyName: 'Adventures Club',
      companyTagline: 'Travel & Tours',
      overview: 'Program Overview',
      travelDates: 'Travel Dates',
      duration: 'Duration',
      travelers: 'Travelers',
      days: 'Days',
      nights: 'Nights',
      people: 'People',
      child: 'Child',
      infant: 'Infant',
      adults: 'Adults',
      day: 'Day',
      activities: 'Activities',
      time: 'Time',
      service: 'Service',
      summary: 'Summary',
      city: 'City',
      units: 'Units',
      nightsCount: 'Nights',
      programPrice: 'Program Price',
      currency: '฿',
      clientAgentInfo: 'Client & Agent Information',
      agentName: 'Agent Name',
      agentCountry: 'Agent Country',
      clientName: 'Client Name',
      phoneNumber: 'Phone Number',
      clientNationality: 'Client Nationality',
      cities: 'Cities',
      requestDate: 'Request Date',
      sendingDate: 'Sending Date',
      itinerary: 'Program Itinerary',
      flightConnections: 'Flight Connections',
      hotelAccommodation: 'Hotel Accommodation',
      checkIn: 'Check-In',
      checkOut: 'Check-Out',
      confirmationNo: 'Confirmation No',
      roomCategory: 'Room Category',
      mealPlan: 'Meal Plan',
      baggage: 'Baggage',
      paxCount: 'Pax',
    },
    ar: {
      companyName: 'Adventures Club',
      companyTagline: 'سفر وسياحة',
      overview: 'نظرة عامة على البرنامج',
      travelDates: 'تواريخ السفر',
      duration: 'المدة',
      travelers: 'المسافرون',
      days: 'أيام',
      nights: 'ليالي',
      people: 'شخص',
      child: 'طفل',
      infant: 'رضيع',
      adults: 'بالغين',
      day: 'اليوم',
      activities: 'الأنشطة',
      time: 'الوقت',
      service: 'الخدمة',
      summary: 'الوصف',
      city: 'المدينة',
      units: 'الوحدات',
      nightsCount: 'الليالي',
      programPrice: 'سعر البرنامج',
      currency: '฿',
      clientAgentInfo: 'معلومات العميل والوكالة',
      agentName: 'اسم الوكالة',
      agentCountry: 'دولة الوكالة',
      clientName: 'اسم العميل',
      phoneNumber: 'رقم الهاتف',
      clientNationality: 'جنسية العميل',
      cities: 'المدن',
      requestDate: 'تاريخ الطلب',
      sendingDate: 'تاريخ الإرسال',
      itinerary: 'برنامج الرحلة',
      flightConnections: 'رحلات الطيران',
      hotelAccommodation: 'أماكن الإقامة بالفنادق',
      checkIn: 'تسجيل الوصول',
      checkOut: 'تسجيل المغادرة',
      confirmationNo: 'رقم التأكيد',
      roomCategory: 'فئة الغرفة',
      mealPlan: 'خطة الوجبة',
      baggage: 'الأمتعة',
      paxCount: 'العدد',
    },
  };

  const texts = translations[lang] || translations.en;

  // ==================== استخراج البيانات ====================

  const title = t(program.title);
  const description = t(program.description);
  const programNumber = program.program_number || program.programNumber || '';

  // Extract all flights and hotels across all days
  const flights = (program.days || []).reduce((acc, day) => {
    const dayFlights = (day.products || []).filter(p => p.product_type === 'flight' || p.productType === 'flight' || p.category_id === 5);
    const mappedFlights = dayFlights.map(f => ({
      ...f,
      departureDate: f.flight_details?.departure_date || day.date || day.start_date
    }));
    return [...acc, ...mappedFlights];
  }, []);
  flights.sort((a, b) => (a.departureDate || '').localeCompare(b.departureDate || ''));

  const hotels = (program.days || []).reduce((acc, day) => {
    const dayHotels = (day.products || []).filter(p => (p.product_type === 'hotel' || p.productType === 'hotel' || p.category_id === 3) && p.hotel_details);
    const mappedHotels = dayHotels.map(h => {
      const checkIn = h.hotel_details.check_in_date || h.check_in_date || day.date || day.start_date;
      const nights = h.nights || h.hotel_details.nights || h.hotel_details.total_room_nights || 1;
      let checkOut = h.hotel_details.check_out_date || h.check_out_date;
      if (!checkOut && checkIn) {
        const d = new Date(checkIn);
        d.setDate(d.getDate() + parseInt(nights));
        checkOut = d.toISOString().split('T')[0];
      }
      return {
        ...h,
        checkIn,
        checkOut,
        nights
      };
    });
    return [...acc, ...mappedHotels];
  }, []);
  hotels.sort((a, b) => (a.checkIn || '').localeCompare(b.checkIn || ''));

  // Helper function to get the correct price and currency
  const getDisplayPrice = () => {
    const price = program.display_price || program.price;
    const currency = program.display_currency || program.currency;
    return {
      price: price ?? 0,
      symbol: currency?.symbol || '฿',
      name: currency?.name?.[lang] || currency?.name?.en || 'THB'
    };
  };

  const { price: totalPrice } = getDisplayPrice();

  const startDate = program.start_date || program.startDate;
  const endDate = program.end_date || program.endDate;
  const daysCount = program.durations || program.days?.length || 0;
  const nightsCount = program.nights || 0;
  const travelersCount = program.number_of_pax || program.numberOfPax || 0;

  const childCount = getPaxQty('child');
  const infantCount = getPaxQty('infant');
  const adultCount = getPaxQty('adult') || getPaxQty('adults');

  const days = program.days || [];

  // ==================== JSX ====================

  return (
    <div className="print-document" dir={lang === 'ar' ? 'rtl' : 'ltr'}>
      {/* ✅ ==================== MODERN LAYERED COVER PAGE ==================== */}
      <div className="print-cover-page-modern">
        {/* Background Image */}
        <img
          src={coverImageUrl}
          alt="Cover Background"
          className="cover-background-img"
        />

        {/* Modern Gradient Overlay with Multiple Layers */}
        <div className="cover-gradient-overlay"></div>

        {/* Main Content Container */}
        <div className="cover-content">
          {/* Top Section - Logo & Branding */}
          <div className="cover-top">
            <div className="cover-logo-container">
              <div className="logo-badge">
                <span className="logo-text">{texts.companyName}</span>

                {/* ✅ المدن بين اسم الشركة والتاجلاين */}
                {clientInfo?.cities && (
                  <div
                    className="cover-cities-top"
                    style={{
                      marginTop: '8px',
                      marginBottom: '8px',
                      fontSize: '13pt',
                      fontWeight: 600,
                      color: '#ffd700',
                      letterSpacing: '1px',
                      textTransform: 'uppercase',
                    }}
                  >
                    {clientInfo.cities}
                  </div>
                )}

                <span className="logo-tagline">{texts.companyTagline}</span>
              </div>
            </div>
          </div>

          {/* Middle Section - Main Title with Decorative Elements */}
          <div className="cover-middle">
            <div className="title-container">
              <div className="decorative-line top"></div>

              {/* ✅ اسم العميل فوق العنوان */}
              {clientInfo?.client_name && (
                <h3 className="program-client-name" style={{
                  fontSize: '20pt',
                  fontWeight: 600,
                  margin: '0 0 12px 0',
                  color: '#fff',
                  textTransform: 'none',
                  whiteSpace: 'nowrap'
                }}>
                  {clientInfo.client_name}
                </h3>
              )}

              <div className="decorative-line bottom"></div>
              <div className="decorative-line top"></div>

              {/* ✅ عنوان البرنامج - سطر واحد */}
              <h1 className="cover-program-title">
                {title || 'Exclusive Travel Program'}
              </h1>

              <div className="decorative-line bottom"></div>
            </div>

            {/* Floating Info Cards */}
            <div className="floating-info-cards">
              <div className="info-card-modern">
                <div className="card-content">
                  <span className="card-label">{texts.duration}</span>
                  <span className="card-value">{daysCount} {texts.days}</span>
                </div>
              </div>

              <div className="info-card-modern">
                <div className="card-content">
                  <span className="card-label">{texts.travelers}</span>
                  <span className="card-value">{travelersCount} {texts.people}</span>
                </div>
              </div>

              <div className="info-card-modern">
                <div className="card-content">
                  <span className="card-label">{lang === 'ar' ? 'الوجهة' : 'Destination'}</span>
                  <span className="card-value">{clientInfo?.cities || formatCities(program.cities, lang) || program.destination || 'Amazing Journey'}</span>
                </div>
              </div>
            </div>
          </div>


          {/* Bottom Section - Date Range */}
          <div className="cover-bottom">
            <div className="date-range-container">
              <div className="date-item">
                <span className="date-label">{lang === 'ar' ? 'تاريخ البدء' : 'Start Date'}</span>
                <span className="date-value">{formatDate(startDate)}</span>
              </div>
              <div className="date-separator">→</div>
              <div className="date-item">
                <span className="date-label">{lang === 'ar' ? 'تاريخ الانتهاء' : 'End Date'}</span>
                <span className="date-value">{formatDate(endDate)}</span>
              </div>
            </div>

            {/* Program Number Badge */}
            <div className="program-number-badge">
              <span className="badge-label">{lang === 'ar' ? 'رقم البرنامج' : 'Program No.'}</span>
              <span className="badge-number">#{programNumber}</span>
            </div>
          </div>
        </div>
      </div>

      {/* ==================== Header ==================== */}
      <div className="print-header">
        <div className="header-left">
          <h1 className="company-name">{texts.companyName}</h1>
          <p className="company-tagline">{texts.companyTagline}</p>
        </div>
        <div className="header-right">
          <span className="program-number">{programNumber}</span>
          <span className="print-date">{formatDate(new Date())}</span>
        </div>
      </div>

      {/* ==================== Program Title ==================== */}
      <div className="program-title">
        <h2>{title || 'Program'}</h2>
        {description && <p className="program-desc">{description}</p>}
      </div>

      {/* ✅ ==================== Client & Agent Information ==================== */}
      {clientInfo && (
        <div className="client-agent-section">
          <h3 className="section-title">{texts.clientAgentInfo}</h3>
          <div className="info-cards-grid">
            {/* Agent Card */}
            <div className="info-card agent-card">
              <div className="card-header">
                {lang === 'ar' ? 'معلومات الوكالة' : 'Agent Information'}
              </div>
              <div className="card-body">
                {clientInfo.agent_name && (
                  <div className="info-row">
                    <span className="info-label">{texts.agentName}:</span>
                    <span className="info-value">{clientInfo.agent_name}</span>
                  </div>
                )}
                {clientInfo.agent_country && (
                  <div className="info-row">
                    <span className="info-label">{texts.agentCountry}:</span>
                    <span className="info-value">{clientInfo.agent_country}</span>
                  </div>
                )}
                {clientInfo.req_date && (
                  <div className="info-row">
                    <span className="info-label">{texts.requestDate}:</span>
                    <span className="info-value">{formatDate(clientInfo.req_date)}</span>
                  </div>
                )}
                {clientInfo.sending_date && (
                  <div className="info-row">
                    <span className="info-label">{texts.sendingDate}:</span>
                    <span className="info-value">{formatDate(clientInfo.sending_date)}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Client Card */}
            <div className="info-card client-card">
              <div className="card-header">
                {lang === 'ar' ? 'معلومات العميل' : 'Client Information'}
              </div>
              <div className="card-body">
                {clientInfo.client_name && (
                  <div className="info-row">
                    <span className="info-label">{texts.clientName}:</span>
                    <span className="info-value">{clientInfo.client_name}</span>
                  </div>
                )}
                {clientInfo.phone_number && (
                  <div className="info-row">
                    <span className="info-label">{texts.phoneNumber}:</span>
                    <span className="info-value" dir={lang === 'ar' ? 'ltr' : 'auto'}>
                      {clientInfo.phone_number}
                    </span>
                  </div>
                )}
                {clientInfo.client_nationality && (
                  <div className="info-row">
                    <span className="info-label">{texts.clientNationality}:</span>
                    <span className="info-value">{clientInfo.client_nationality}</span>
                  </div>
                )}
                {clientInfo.cities && (
                  <div className="info-row">
                    <span className="info-label">{texts.cities}:</span>
                    <span className="info-value">{clientInfo.cities}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ==================== Program Overview ==================== */}
      <div className="overview-section">
        <h3 className="section-title">{texts.overview}</h3>
        <div className="overview-grid">
          <div className="info-item">
            <span className="label">{texts.travelDates}</span>
            <span className="value">
              {formatDate(startDate)} → {formatDate(endDate)}
            </span>
          </div>
          <div className="info-item">
            <span className="label">{texts.duration}</span>
            <span className="value">
              {daysCount} {texts.days} / {nightsCount} {texts.nights}
            </span>
          </div>
          <div className="info-item">
            <span className="label">{texts.travelers}</span>
            <span className="value">{travelersCount} {texts.people}</span>
          </div>
          <div className="info-item">
            <span className="label">{texts.travelers}</span>
            <span className="value">
              {adultCount} {texts.adults} • {childCount} {texts.child} • {infantCount} {texts.infant}
            </span>
          </div>
        </div>

        {/* Travelers Table */}
        <div className="travelers-table" style={{ marginTop: '8px' }}>
          <table>
            <thead>
              <tr>
                <th>{texts.adults}</th>
                <th>{texts.child}</th>
                <th>{texts.infant}</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>{adultCount}</td>
                <td>{childCount}</td>
                <td>{infantCount}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* ==================== Flight Connections ==================== */}
      {flights && flights.length > 0 && (
        <div className="flight-connections-section">
          <h3 className="section-title">{texts.flightConnections}</h3>
          <div className="flight-grid">
            {flights.map((flight, idx) => {
              const details = flight.flight_details || {};
              const airlineName = details.airline?.name?.[lang] || details.airline?.name?.en || '';
              const depAirport = details.departure_airport || {};
              const arrAirport = details.arrival_airport || {};
              const depCity = depAirport.city?.name?.[lang] || depAirport.city?.name?.en || '';
              const arrCity = arrAirport.city?.name?.[lang] || arrAirport.city?.name?.en || '';

              return (
                <div key={idx} className="flight-card">
                  <div className="flight-card-header">
                    <span className="flight-card-date">{formatDate(flight.departureDate)}</span>
                    <span className="flight-card-airline">{airlineName}</span>
                  </div>
                  <div className="flight-card-route">
                    {depCity} {depAirport.code ? `(${depAirport.code})` : ''} &rarr; {arrCity} {arrAirport.code ? `(${arrAirport.code})` : ''}
                  </div>
                  <div className="flight-card-times">
                    <div className="time-block">
                      <span className="time-val">{formatTime(details.departure_time)}</span>
                      <span className="airport-code">{depAirport.code}</span>
                    </div>
                    <div className="flight-line-container">
                      <div className="flight-line"></div>
                      <span className="flight-no-label">{details.flight_number}</span>
                    </div>
                    <div className="time-block">
                      <span className="time-val">{formatTime(details.arrival_time)}</span>
                      <span className="airport-code">{arrAirport.code}</span>
                    </div>
                  </div>
                  <div className="flight-card-footer">
                    <span>PNR: {details.pnr || '-'}</span>
                    <span>{texts.baggage}: {details.baggage_weight || '-'} {details.baggage_unit || 'kg'}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ==================== Hotel Accommodation ==================== */}
      {hotels && hotels.length > 0 && (
        <div className="hotel-accommodation-section">
          <h3 className="section-title">{texts.hotelAccommodation}</h3>
          <div className="hotel-accommodation-grid">
            {hotels.map((hotel, idx) => {
              const details = hotel.hotel_details || {};
              const cityName = details.city?.name?.[lang] || details.city?.name?.en || '';
              const stars = details.rating?.stars || 0;
              const mealPlanName = details.meal_plan?.name?.[lang] || details.meal_plan?.name?.en || '';
              const roomCategoryName = details.room_category?.name?.[lang] || details.room_category?.name?.en || '';
              const confNumber = details.confirmation_number || program.confirmation_number || '';

              return (
                <div key={idx} className="hotel-card">
                  <div className="hotel-card-image-container">
                    <img 
                      src={program.cover_image?.url || "https://testapi.ztripplus.com/storage/1146/conversions/6a3117e14e6fe_images_(1)-preview.jpg"} 
                      alt="Hotel" 
                      className="hotel-card-image"
                    />
                  </div>
                  <div className="hotel-card-content">
                    <div className="hotel-card-header">
                      <div className="hotel-card-title-section">
                        {cityName && <span className="hotel-card-city-badge">{cityName}</span>}
                        <h4 className="hotel-card-name">{details.hotel_name}</h4>
                      </div>
                      <div className="hotel-card-stars">
                        {Array.from({ length: stars }).map((_, i) => (
                          <span key={i}>★</span>
                        ))}
                      </div>
                    </div>

                    <div className="hotel-card-dates">
                      <div className="date-box">
                        <span className="date-box-label">{texts.checkIn}</span>
                        <span className="date-box-val">{formatDate(hotel.checkIn)}</span>
                      </div>
                      <div className="date-box">
                        <span className="date-box-label">{texts.checkOut}</span>
                        <span className="date-box-val">{formatDate(hotel.checkOut)}</span>
                      </div>
                    </div>

                    <div className="hotel-card-details">
                      {confNumber && (
                        <div className="hotel-detail-row">
                          <span className="hotel-detail-icon">✓</span>
                          <span>{texts.confirmationNo}: <strong>{confNumber}</strong></span>
                        </div>
                      )}
                      {roomCategoryName && (
                        <div className="hotel-detail-row">
                          <span className="hotel-detail-icon">🛏️</span>
                          <span>{texts.roomCategory}: <strong>{roomCategoryName}</strong></span>
                        </div>
                      )}
                      {mealPlanName && (
                        <div className="hotel-detail-row">
                          <span className="hotel-detail-icon">🍽️</span>
                          <span>{texts.mealPlan}: <strong>{mealPlanName}</strong></span>
                        </div>
                      )}
                    </div>

                    <span className="hotel-nights-badge">
                      {hotel.nights} {texts.nightsCount}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ==================== Program Itinerary ==================== */}
      {days && days.length > 0 && (
        <div className="itinerary-section">
          <h3 className="section-title">{texts.itinerary}</h3>

          {days.map((day, dayIndex) => (
            <div
              key={dayIndex}
              className={`day-card ${isDayLarge(day) ? 'large-day' : ''}`}
            >
              {/* Day Header */}
              <div className="day-header">
                <span className="day-number">
                  {lang === 'ar' ? `اليوم ${dayIndex + 1}` : `Day ${dayIndex + 1}`}
                </span>
                <span className="day-date">
                  {formatDate(day.date || day.start_date)}
                </span>
              </div>

              {/* Day Content */}
              <div className="day-content">
                {/* Day Title */}
                {day.title && (
                  <h4 className="day-title">{t(day.title)}</h4>
                )}

                {/* Day Description */}
                {day.description && (
                  <p className="day-description">{t(day.description)}</p>
                )}

                {/* Activities/Products */}
                {day.products && day.products.length > 0 && (
                  <div className="activities">
                    <h5>
                      {lang === 'ar' ? ' الأنشطة والخدمات' : ' Activities & Services'}
                    </h5>
                    <table className="day-products-table">
                      <thead>
                        <tr>
                          <th style={{ width: '10%' }}>{texts.time}</th>
                          <th style={{ width: '23%' }}>{texts.service}</th>
                          <th style={{ width: '25%' }}>{texts.summary}</th>
                          <th style={{ width: '13%' }}>{texts.city}</th>
                          <th style={{ width: '10%' }}>{texts.paxCount}</th>
                          <th style={{ width: '9%' }}>{texts.units}</th>
                          <th style={{ width: '10%' }}>{texts.nightsCount}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {day.products.map((product, prodIndex) => {
                          const pricingCode = getPricingCode(product);
                          const city = getProductCity(product);
                          const summary = getProductSummary(product);
                          const isFlight = product.product_type === 'flight' || product.productType === 'flight' || product.category_id === 5;
                          const isHotel = product.product_type === 'hotel' || product.productType === 'hotel' || product.category_id === 3;

                          const getFlightRouteStr = () => {
                            const details = product.flight_details || {};
                            const depAirport = details.departure_airport || {};
                            const arrAirport = details.arrival_airport || {};
                            const depCity = depAirport.city?.name?.[lang] || depAirport.city?.name?.en || '';
                            const arrCity = arrAirport.city?.name?.[lang] || arrAirport.city?.name?.en || '';
                            if (!depCity && !arrCity) return '-';
                            return `${depCity}${depAirport.code ? ` (${depAirport.code})` : ''} → ${arrCity}${arrAirport.code ? ` (${arrAirport.code})` : ''}`;
                          };

                          const getPaxValue = () => {
                            if (isFlight || pricingCode === 'per_person' || pricingCode === 'per_person_night') {
                              return travelersCount;
                            }
                            if (isHotel) {
                              return product.rooms_count || product.units || '-';
                            }
                            return '-';
                          };

                          return (
                            <tr key={prodIndex}>
                              <td className="cell-time">{getProductTime(product)}</td>
                              <td className="cell-service">
                                <strong>{isHotel && product.hotel_details?.hotel_name ? product.hotel_details.hotel_name : t(product.title)}</strong>
                              </td>
                              <td className="cell-summary">
                                {summary && (
                                  <div
                                    dangerouslySetInnerHTML={{
                                      __html: summary,
                                    }}
                                  />
                                )}
                              </td>
                              <td className="cell-city">
                                {isFlight ? (
                                  getFlightRouteStr()
                                ) : (
                                  (pricingCode === 'per_person' || isHotel) && city ? `\u2068${city}\u2069` : '-'
                                )}
                              </td>
                              <td className="cell-pax">{getPaxValue()}</td>
                              <td className="cell-units">{product.units ?? '-'}</td>
                              <td className="cell-nights">{product.nights ?? '-'}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ✅ ==================== PDF Custom Sections ==================== */}
      {pdfSections && Object.keys(pdfSections).length > 0 && (
        <div className="custom-sections">
          {Object.entries(pdfSections).map(([key, section]) => (
            <div key={key} className="pdf-custom-section">
              <h3 className="section-title">{section.title || key}</h3>
              {section.items && section.items.length > 0 && (
                <ul className="section-items-list">
                  {section.items.map((item, idx) => (
                    <li key={idx}>{t(item)}</li>
                  ))}
                </ul>
              )}
              {section.note && (
                <p className="section-note">
                  <strong> {lang === 'ar' ? 'ملاحظة' : 'Note'}:</strong> {section.note}
                </p>
              )}
            </div>
          ))}
        </div>
      )}

      {/* ✅ ==================== Pricing Section ==================== */}
      {includePrices && totalPrice > 0 && (
        <div className="pricing-section">
          <div className="total-price" dir={lang === 'ar' ? 'ltr' : 'rtl'}>
            <span className="price-amount">
              {formatCurrency(totalPrice)} {texts.currency}
            </span>
            <span>
              {lang === 'ar' ? 'السعر الإجمالي للبرنامج' : 'Total Program Price'}
            </span>
          </div>
        </div>
      )}

      {/* ==================== Footer ==================== */}
      <div className="print-footer">
        <p className="print-footer-text">
          {texts.companyName} • {texts.companyTagline}
        </p>
        <p className="print-footer-text" style={{ fontSize: '6pt', marginTop: '2px' }}>
          {lang === 'ar'
            ? 'تم إنشاء هذا البرنامج بواسطة نظام Adventures Club'
            : 'Generated by Adventures Club System'}
        </p>
      </div>
    </div>
  );
};

export default PrintProgram;
